<template>
  <Button :disabled="disabled" @click="displayButton.onClick" :variant="displayButton.variant">
    {{ displayButton.text }}
  </Button>
</template>

<script setup lang="ts">
import { PositionCode } from '@rendora/common/entities/analysis_backend';
import { showDynamicContact } from '@rendora/common/features/contact';
import { showBuyEquityModal } from '@rendora/common/features/payment/ui/buy-equity';
import { getI18nService } from '@rendora/common/services/i18n';
import { useUserStore } from '@rendora/common/store/user';
import { IRightsType } from '@rendora/common/types/enums';
import { isUS } from '@rendora/common/utils/config';
import { Button, ButtonProps } from '@rendora/design/components/button/index';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { showUpgradeEquityModal } from '../../../upgrade-equity';

const props = defineProps<{
  rightType: number;
}>();

const i18nService = getI18nService();
const $t = i18nService.t;
const userStore = useUserStore();
const { user } = storeToRefs(userStore);

// 个人体验版  个人入门版  个人标准版  企业版
const rightType = computed(() => props.rightType);

// 按钮配置类型
type ButtonConfig = {
  variant: ButtonProps['variant'];
  text: string;
  disabled: boolean;
  onClick?: () => void;
};

// 检查是否为续费场景（用户已有相同权限类型且不是体验版）
const isRenewalScenario = computed(() => {
  const currentRightsType = user.value?.rights_type;
  return currentRightsType === props.rightType && currentRightsType !== IRightsType.TASTE;
});

// 获取个人入门版按钮配置
const getPersonalLiteButton = (): ButtonConfig => {
  const isOrgFlagship = user.value?.rights_type === IRightsType.ORG_FLAGSHIP;
  return {
    variant: 'warning',
    text: isOrgFlagship ? $t('li-ji-gou-mai') : $t('li-ji-ding-yue'),
    disabled: false,
    onClick: () => {
      showBuyEquityModal({ targetType: IRightsType.PERSONAL_LITE });
    },
  };
};

// 获取团队轻量版按钮配置
const getTeamLightButton = (): ButtonConfig => {
  if (isUS()) {
    return {
      variant: 'warning-10',
      text: $t('li-ji-gou-mai'),
      disabled: false,
      onClick: () => {
        showBuyEquityModal({ targetType: IRightsType.TEAM_LIGHT });
      },
    };
  }
  return {
    variant: 'warning-10',
    text: $t('zi-xun-gu-wen'),
    disabled: false,
    onClick: () => {
      showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
    },
  };
};

// 获取个人标准版按钮配置
const getPersonalButton = (): ButtonConfig => {
  const isFromPersonalLite = user.value?.rights_type === IRightsType.PERSONAL_LITE;
  return {
    variant: 'warning',
    text: isFromPersonalLite ? `${$t('li-ji-ding-yue')}/${$t('sheng-ji')}` : $t('li-ji-ding-yue'),
    disabled: false,
    onClick: () => {
      const title = isFromPersonalLite ? $t('ge-ren-ban') + $t('gou-mai-tao-can') : '';
      if (isFromPersonalLite) {
        showUpgradeEquityModal();
      } else {
        showBuyEquityModal({ targetType: IRightsType.PERSONAL, title });
      }
    },
  };
};

// 获取企业定制版按钮配置
const getOrgCustomButton = (): ButtonConfig => {
  return {
    variant: 'warning-10',
    text: $t('zi-xun-gu-wen'),
    disabled: false,
    onClick: () => {
      showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
    },
  };
};

// 根据权限类型获取按钮配置
const getButtonConfigByRightType = (type: IRightsType): ButtonConfig | undefined => {
  const configMap: Partial<Record<IRightsType, () => ButtonConfig>> = {
    [IRightsType.TASTE]: () => ({
      variant: 'back',
      text: $t('yi-jing-shen-qing-ti-yan'),
      disabled: true,
    }),
    [IRightsType.PERSONAL_LITE]: getPersonalLiteButton,
    [IRightsType.PERSONAL]: getPersonalButton,
    [IRightsType.TEAM_LIGHT]: getTeamLightButton,
    [IRightsType.ORG_CUSTOM]: getOrgCustomButton,
  };

  const getter = configMap[type];
  return getter ? getter() : undefined;
};

const displayButton = computed<ButtonConfig>(() => {
  // 续费场景
  if (isRenewalScenario.value) {
    return {
      variant: 'warning',
      text: $t('li-ji-xu-fei'),
      disabled: false,
      onClick: () => {
        showBuyEquityModal();
      },
    };
  }

  // 根据权限类型获取配置
  const config = getButtonConfigByRightType(props.rightType as IRightsType);
  if (config) {
    return config;
  }

  // 默认配置
  return {
    variant: 'purple',
    text: $t('li-ji-gou-mai'),
    disabled: false,
  };
});

const disabled = computed(() => rightType.value === IRightsType.TASTE);
</script>

<style scoped lang="scss">
.right-btn {
  font-size: 14px;
}
</style>
